import React, { useEffect, useState } from "react";
import { Button, Dropdown, Menu, message } from "antd";
import { DownOutlined } from "@ant-design/icons";
import { useDispatch } from "react-redux";
import { showModal } from "../../../redux/actions/modal";
import { postBlobData } from "../../../newApis/dotNetApiMethods";
import { errorHandler } from "../../../helpers/errorHandler";
import * as ajaxEndpoints from "../../../api/ajax-endpoints";
import CustomInputField from "../../CustomHTMLElements/CustomInputField";
import { formatMoney } from "../../../helpers/formatter";
import { formatTenor } from "../../../helpers/formatTenor";
import { appInsights } from "../../../config/appInsights";

/** "2026-10-08T00:00:00" → "2026-10-08"; a missing date reads as a dash. */
const toDate = (value) => (value ? String(value).substring(0, 10) : "—");

const InvestmentDetails = ({ investment, customerEmail }) => {
  const {
    id,
    currency,
    investment_product_name,
    tenure,
    tenor_unit,
    interest_rate,
    interest_repayment_type,
    deposit_type,
    investment_status,
    is_roll_over,
    rollOverType,
    effective_date,
    maturity_date,
    days_remaining,
    lock_in_period,
    grace_period,
    next_withdrawal_window_start_date,
    next_withdrawal_window_end_date,
    target_amount,
    principal_amount,
    total_deposits,
    total_withdrawals,
    interest_accrued,
    interest_amount,
    withholding_tax_amount,
    net_interest,
    maturity_amount,
    total_settled,
    outstanding_settlement,
    maturityInstruction,
    parent_investment_reference,
    child_investment_reference,
  } = investment;

  const money = (value) => formatMoney(value ?? 0, currency);

  const dispatch = useDispatch();
  const [isLoadingCertificate, setIsLoadingCertificate] = useState(false);

  useEffect(() => {
    appInsights.trackPageView({
      name: "Investment Details  - (InvestmentDetails.jsx)",
      isLoggedIn: true,
    });
  }, []);

  const handleViewCertificate = async () => {
    setIsLoadingCertificate(true);
    try {
      const requestBody = {
        investmentId: id,
        isDownload: true,
      };

      const response = await postBlobData(
        ajaxEndpoints.GET_INVESTMENT_CERTIFICATE,
        requestBody,
      );

      const blob = response?.data;
      const certificateUrl = window.URL.createObjectURL(blob);

      dispatch(
        showModal({
          modalIdentifier: "viewinvestmentcertificate",
          dataRecord: {
            investmentId: id,
            currency: currency,
            customerEmail: customerEmail || "",
            certificateUrl: certificateUrl,
          },
        }),
      );
    } catch (error) {
      message.error(errorHandler(error));
    } finally {
      setIsLoadingCertificate(false);
    }
  };

  const handleSendCertificate = () => {
    dispatch(
      showModal({
        modalIdentifier: "sendinvestmentcertificate",
        dataRecord: {
          investmentId: id,
          currency: currency,
          customerEmail: customerEmail || "",
        },
      }),
    );
  };

  const handleMenuClick = (key) => {
    switch (key) {
      case "view":
        return handleViewCertificate();
      case "send":
        return handleSendCertificate();
      default:
        break;
    }
  };

  const menu = (
    <Menu
      onClick={({ key }) => handleMenuClick(key)}
      items={[
        {
          key: "view",
          label: "View Certificate",
          icon: <i className="fa fa-eye" />,
        },
        {
          key: "send",
          label: "Send Certificate",
          icon: <i className="fa fa-paper-plane" />,
        },
      ]}
    />
  );

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center my-4">
        <h6 className="page-subtitle mb-0">Investment Details</h6>
        <Dropdown overlay={menu} trigger={["click"]}>
          <Button type="primary" size="large" loading={isLoadingCertificate}>
            Take Action <DownOutlined />
          </Button>
        </Dropdown>
      </div>
      <form>
        <div className="row">
          <div className="col-md-6">
            <CustomInputField
              label="Investment Fund"
              defaultValue={investment_product_name}
              readOnly={true}
            />
          </div>
          <div className="col-md-6">
            <CustomInputField
              label="Investment Tenor"
              defaultValue={formatTenor(tenure, tenor_unit)}
              readOnly={true}
            />
          </div>
          <div className="col-md-6">
            <CustomInputField
              label="Interest Rate"
              defaultValue={`${interest_rate}%`}
              readOnly={true}
            />
          </div>
          <div className="col-md-6">
            <CustomInputField
              label="Repayment Type"
              defaultValue={interest_repayment_type}
              readOnly={true}
            />
          </div>
          <div className="col-md-6">
            <CustomInputField
              label="Deposit Type"
              defaultValue={deposit_type}
              readOnly={true}
            />
          </div>
          <div className="col-md-6">
            <CustomInputField
              label="Status"
              defaultValue={investment_status}
              readOnly={true}
            />
          </div>
          <div className="col-md-6">
            <CustomInputField
              label="Is Roll Over?"
              defaultValue={is_roll_over ? "Yes" : "No"}
              readOnly={true}
            />
          </div>
          {is_roll_over && (
            <div className="col-md-6">
              <CustomInputField
                label="Roll Over Type"
                defaultValue={rollOverType}
                readOnly={true}
              />
            </div>
          )}

          <div className="col-md-12">
            <hr />
          </div>
          <div className="col-md-6">
            <CustomInputField
              label="Date Invested"
              defaultValue={toDate(effective_date)}
              readOnly={true}
            />
          </div>
          <div className="col-md-6">
            <CustomInputField
              label="Maturity Date"
              defaultValue={toDate(maturity_date)}
              readOnly={true}
            />
          </div>
          <div className="col-md-6">
            <CustomInputField
              label="Days Remaining"
              defaultValue={days_remaining ?? "—"}
              readOnly={true}
            />
          </div>
          {lock_in_period ? (
            <div className="col-md-6">
              <CustomInputField
                label="Lock-in Period"
                defaultValue={`${lock_in_period} days`}
                readOnly={true}
              />
            </div>
          ) : null}
          {grace_period ? (
            <div className="col-md-6">
              <CustomInputField
                label="Withdrawal Window Length"
                defaultValue={`${grace_period} days`}
                readOnly={true}
              />
            </div>
          ) : null}
          {next_withdrawal_window_start_date ? (
            <div className="col-md-6">
              <CustomInputField
                label="Next Withdrawal Window"
                defaultValue={`${toDate(next_withdrawal_window_start_date)} – ${toDate(next_withdrawal_window_end_date)}`}
                readOnly={true}
              />
            </div>
          ) : null}
          {target_amount ? (
            <div className="col-md-6">
              <CustomInputField
                label="Target Amount"
                defaultValue={money(target_amount)}
                readOnly={true}
              />
            </div>
          ) : null}

          <div className="col-md-12">
            <hr />
          </div>
          <div className="col-md-6">
            <CustomInputField
              label="Amount Invested"
              defaultValue={money(principal_amount)}
              readOnly={true}
            />
          </div>
          <div className="col-md-6">
            <CustomInputField
              label="Total Deposits"
              defaultValue={money(total_deposits)}
              readOnly={true}
            />
          </div>
          <div className="col-md-6">
            <CustomInputField
              label="Total Withdrawals"
              defaultValue={money(total_withdrawals)}
              readOnly={true}
            />
          </div>
          <div className="col-md-6">
            <CustomInputField
              label="Interest Accrued"
              defaultValue={money(interest_accrued)}
              readOnly={true}
            />
          </div>
          <div className="col-md-6">
            <CustomInputField
              label="Interest Settled"
              defaultValue={money(total_settled)}
              readOnly={true}
            />
          </div>
          <div className="col-md-6">
            <CustomInputField
              label="Outstanding Settlement"
              defaultValue={money(outstanding_settlement)}
              readOnly={true}
            />
          </div>

          <div className="col-md-12">
            <hr />
            <small className="text-muted">
              Estimated — core banking posts the actual figures, which can
              differ after withdrawals and charges.
            </small>
          </div>
          <div className="col-md-6">
            <CustomInputField
              label="Estimated Interest At Maturity"
              defaultValue={money(interest_amount)}
              readOnly={true}
            />
          </div>
          <div className="col-md-6">
            <CustomInputField
              label="Estimated Withholding Tax"
              defaultValue={money(withholding_tax_amount)}
              readOnly={true}
            />
          </div>
          <div className="col-md-6">
            <CustomInputField
              label="Estimated Net Interest"
              defaultValue={money(net_interest)}
              readOnly={true}
            />
          </div>
          <div className="col-md-6">
            <CustomInputField
              label="Estimated Total At Maturity"
              defaultValue={money(maturity_amount)}
              readOnly={true}
            />
          </div>
          <div className="col-md-12">
            <hr />
          </div>
          <div className="col-md-6">
            <CustomInputField
              label="Maturity Instruction"
              defaultValue={maturityInstruction ?? "—"}
              readOnly={true}
            />
          </div>
          <div className="col-md-6">
            <CustomInputField
              label="Parent Investment Reference"
              defaultValue={parent_investment_reference ?? "—"}
              readOnly={true}
            />
          </div>
          <div className="col-md-6">
            <CustomInputField
              label="Child Investment Reference"
              defaultValue={child_investment_reference ?? "—"}
              readOnly={true}
            />
          </div>
        </div>
      </form>
    </div>
  );
};

export default InvestmentDetails;
